import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PortfolioRiskItem {
  id: string;
  file_name: string;
  aging_hours: number;
  final_decision: string | null;
  reviewed_by_name: string | null;
}

export interface UnaddressedAlertItem {
  contract_id: string;
  title: string;
  alert_count: number;
}

export interface ExpiringContractItem {
  contract_id: string;
  title: string;
  counterparty: string | null;
  end_date: string;
  days_remaining: number;
}

export interface SoftFlagRow {
  flag_key: string;
  label: string;
  count: number;
}

export interface PortfolioRiskData {
  overdue_review_count: number;
  overdue_review_items: PortfolioRiskItem[];
  unaddressed_alerts_count: number;
  unaddressed_alerts_items: UnaddressedAlertItem[];
  expiring_contracts_count: number;
  expiring_contracts_items: ExpiringContractItem[];
  soft_flag_rows: SoftFlagRow[];
  refreshed_at: string;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

const QUERY_KEY = ['dashboard', 'portfolio-risk'] as const;

/**
 * Fetches aggregated portfolio risk data from GET /api/v1/dashboard/portfolio-risk.
 * Returns four widget datasets: overdue reviews, unaddressed alerts,
 * expiring contracts (within 30 days), and soft-flag summary.
 *
 * FR-086: Cross-contract risk view. All data scoped to client_id via RLS.
 */
export function usePortfolioRisk() {
  return useQuery<PortfolioRiskData>({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<PortfolioRiskData>('/api/v1/dashboard/portfolio-risk'),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}

/**
 * Returns an imperative refetch function for the portfolio risk query.
 * Used by the manual Refresh button.
 */
export function useRefreshPortfolioRisk() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: QUERY_KEY });
}
